import { useEffect, useState } from 'react';
import {
  Plus, Save, Pencil, Trash2, Power, EyeOff, Ticket, Percent, DollarSign, Calendar,
  Eye,
} from 'lucide-react';
import supabase from '../lib/supabase';
import {
  PageHeader, Section, Badge, Button, EmptyState, Modal, Field, Input, Select, StatusLine,
} from '../components/ui';

type Promo = {
  id: string;
  code: string;
  description: string | null;
  display_label: string | null;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  max_uses: number | null;
  uses_count: number;
  valid_from: string;
  valid_until: string | null;
  is_active: boolean;
  is_public: boolean;
  club_id: string | null;
  venue_id: string | null;
};

export default function Promos() {
  const [rows, setRows] = useState<Promo[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Promo | 'new' | null>(null);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('promo_codes')
      .select('*')
      .order('created_at', { ascending: false });
    setRows((data as Promo[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (p: Promo) => {
    await supabase.from('promo_codes').update({ is_active: !p.is_active, updated_at: new Date().toISOString() }).eq('id', p.id);
    await supabase.rpc('audit_log', {
      p_action: p.is_active ? 'promo_disabled' : 'promo_enabled',
      p_target_type: 'promo_code', p_target_id: p.id,
      p_metadata: { code: p.code } as any,
    });
    load();
  };

  const del = async (p: Promo) => {
    if (p.uses_count > 0) {
      alert(`Can't delete ${p.code}: already used ${p.uses_count} time(s). Disable it instead to hide from customers while preserving the audit trail.`);
      return;
    }
    if (!confirm(`Delete promo ${p.code}?`)) return;
    await supabase.from('promo_codes').delete().eq('id', p.id);
    await supabase.rpc('audit_log', {
      p_action: 'promo_deleted',
      p_target_type: 'promo_code', p_target_id: p.id,
      p_metadata: { code: p.code } as any,
    });
    load();
  };

  return (
    <div className="p-8 max-w-6xl">
      <PageHeader
        eyebrow="AceRent admin"
        title="Promo codes"
        subtitle="Discount codes customers can apply at checkout."
        actions={<Button icon={Plus} onClick={() => setEditing('new')}>New code</Button>}
      />

      <Section className="p-0 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading promos…</div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Ticket}
            title="No promo codes yet"
            description="Create your first code — percentage off, flat off, or a one-off launch promo."
            action={<Button icon={Plus} onClick={() => setEditing('new')}>Create a promo</Button>}
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50/70 text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Code</th>
                <th className="text-left px-5 py-3 font-semibold">Discount</th>
                <th className="text-left px-5 py-3 font-semibold">Usage</th>
                <th className="text-left px-5 py-3 font-semibold">Valid</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => {
                const exhausted = p.max_uses !== null && p.uses_count >= p.max_uses;
                const expired = p.valid_until ? new Date(p.valid_until) < new Date() : false;
                return (
                  <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50/60">
                    <td className="px-5 py-3">
                      <div className="font-mono font-semibold text-gray-900">{p.code}</div>
                      {p.description && (
                        <div className="text-xs text-gray-500 max-w-sm truncate">{p.description}</div>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      {p.discount_type === 'percentage'
                        ? <span className="inline-flex items-center gap-1"><Percent className="w-3 h-3" />{Number(p.discount_value).toFixed(0)}%</span>
                        : <span className="inline-flex items-center gap-1"><DollarSign className="w-3 h-3" />AED {Number(p.discount_value).toFixed(0)}</span>}
                    </td>
                    <td className="px-5 py-3">
                      {p.uses_count}
                      {p.max_uses !== null ? ` / ${p.max_uses}` : ''}
                      {exhausted && <div className="text-[11px] text-red-700 mt-0.5">Exhausted</div>}
                    </td>
                    <td className="px-5 py-3 text-xs text-gray-600">
                      <div className="flex items-center gap-1"><Calendar className="w-3 h-3" />
                        From {fmtDate(p.valid_from)}
                      </div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <Calendar className="w-3 h-3" />
                        {p.valid_until ? `Until ${fmtDate(p.valid_until)}` : 'No expiry'}
                      </div>
                      {expired && <div className="text-[11px] text-red-700 mt-0.5">Expired</div>}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-col gap-1.5 items-start">
                        <button onClick={() => toggleActive(p)}>
                          {p.is_active && !expired && !exhausted
                            ? <Badge tone="green" icon={Power}>Active</Badge>
                            : p.is_active
                              ? <Badge tone="amber">Inactive reason</Badge>
                              : <Badge tone="gray" icon={EyeOff}>Hidden</Badge>}
                        </button>
                        {p.is_public
                          ? <Badge tone="lime" icon={Eye}>On brand page</Badge>
                          : <Badge tone="gray" icon={EyeOff}>Code-only</Badge>}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="secondary" icon={Pencil} onClick={() => setEditing(p)}>Edit</Button>
                        <Button size="sm" variant="danger" icon={Trash2} onClick={() => del(p)}>Delete</Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Section>

      {editing && (
        <PromoForm
          promo={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function PromoForm({
  promo, onClose, onSaved,
}: {
  promo: Promo | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = !promo;
  const [code, setCode] = useState(promo?.code ?? '');
  const [description, setDescription] = useState(promo?.description ?? '');
  const [displayLabel, setDisplayLabel] = useState(promo?.display_label ?? '');
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed'>(promo?.discount_type ?? 'percentage');
  const [discountValue, setDiscountValue] = useState(String(promo?.discount_value ?? '10'));
  const [maxUses, setMaxUses] = useState(promo?.max_uses != null ? String(promo.max_uses) : '');
  const [validFrom, setValidFrom] = useState(
    (promo?.valid_from ?? new Date().toISOString()).slice(0, 10),
  );
  const [validUntil, setValidUntil] = useState(
    promo?.valid_until ? promo.valid_until.slice(0, 10) : '',
  );
  const [isActive, setIsActive] = useState(promo?.is_active ?? true);
  const [isPublic, setIsPublic] = useState(promo?.is_public ?? false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    const payload: any = {
      code: code.trim().toUpperCase(),
      description: description || null,
      display_label: displayLabel.trim() || null,
      discount_type: discountType,
      discount_value: Number(discountValue),
      max_uses: maxUses ? Number(maxUses) : null,
      valid_from: new Date(validFrom + 'T00:00:00Z').toISOString(),
      valid_until: validUntil ? new Date(validUntil + 'T23:59:59Z').toISOString() : null,
      is_active: isActive,
      is_public: isPublic,
    };
    const q = isNew
      ? supabase.from('promo_codes').insert(payload).select('id').single()
      : supabase.from('promo_codes').update(payload).eq('id', promo!.id).select('id').single();
    const { data, error } = await q;
    setBusy(false);
    if (error) { setErr(error.message); return; }
    await supabase.rpc('audit_log', {
      p_action: isNew ? 'promo_created' : 'promo_updated',
      p_target_type: 'promo_code', p_target_id: data?.id ?? promo?.id ?? null,
      p_metadata: { code: payload.code, discount_type: payload.discount_type,
                    discount_value: payload.discount_value } as any,
    });
    onSaved();
  };

  return (
    <Modal
      title={isNew ? 'New promo code' : `Edit ${promo?.code}`}
      subtitle="Customers apply the code at checkout to get the discount."
      onClose={onClose}
      wide
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Code" hint="Auto-uppercased on save. No spaces.">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\s/g, '').toUpperCase())}
              required placeholder="WELCOME25"
              className="font-mono uppercase tracking-wider"
            />
          </Field>
          <Field label="Description" hint="Shown to admins; not to customers.">
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Launch promo for new signups" />
          </Field>
        </div>

        <Field label="Public headline" hint='Shown on the customer brand page when "On brand page" is on. Falls back to "20% off" / "AED 50 off".'>
          <Input value={displayLabel} onChange={(e) => setDisplayLabel(e.target.value)}
            placeholder="20% off your first court booking" />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Discount type">
            <Select value={discountType} onChange={(e) => setDiscountType(e.target.value as any)}>
              <option value="percentage">Percentage off</option>
              <option value="fixed">Flat AED off</option>
            </Select>
          </Field>
          <Field label={discountType === 'percentage' ? 'Percent off' : 'Amount off (AED)'}>
            <div className="relative">
              {discountType === 'percentage'
                ? <Percent className="w-3.5 h-3.5 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />
                : <DollarSign className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />}
              <Input
                type="number" min="0" step="1"
                value={discountValue} onChange={(e) => setDiscountValue(e.target.value)}
                required
                className={discountType === 'percentage' ? 'pr-9' : 'pl-9'}
              />
            </div>
          </Field>
          <Field label="Max uses" hint="Leave blank for unlimited">
            <Input type="number" min="1" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} placeholder="e.g. 100" />
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Valid from">
            <Input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} required />
          </Field>
          <Field label="Valid until" hint="Leave blank for no expiry">
            <Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
          </Field>
        </div>

        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)}
              className="w-4 h-4 accent-[#2d5016]" />
            Active (redeems at checkout)
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)}
              className="w-4 h-4 accent-[#2d5016]" />
            On brand page (surfaced as a tap-to-copy card on the club's customer page)
          </label>
        </div>

        {err && <StatusLine kind="error">{err}</StatusLine>}

        <div className="flex gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} className="flex-1">Cancel</Button>
          <Button type="submit" loading={busy} icon={Save} className="flex-1">
            {isNew ? 'Create code' : 'Save changes'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

